import { ref, unref } from 'vue';

/**
 * Team/user data and the assignee PUT for the team-assignment dialog.
 *
 * `teamUsers` is keyed by team id. Building it needs two tables: `team_users`
 * has the membership and roles, `users` has the avatar URLs, so the two are
 * merged here.
 *
 * @param {object} deps
 * @param {object|import('vue').Ref} deps.conn        DuckDB connection
 * @param {object|import('vue').Ref} deps.apiClient   the app's ApiClient (PUT)
 * @param {string|import('vue').Ref} deps.apiUrl      GA event payload only
 *   (the ApiClient is stateless and no longer carries it)
 * @param {string|import('vue').Ref} deps.projectCode used in the assignee path
 * @param {string|import('vue').Ref} [deps.projectName] GA event payload only
 * @param {(name: string, params?: object) => void} [deps.trackEvent]
 */
export function useTeamAssignment({ conn, apiClient, apiUrl, projectCode, projectName, trackEvent }) {
  const teamUsers = ref({});
  const assigning = ref(false);

  const loadTeamUsers = async () => {
    if (Object.keys(teamUsers.value).length > 0) return; // Already loaded
    try {
      const connection = unref(conn);

      // Step 1: Load avatar URLs from users table (has Key-Value structure)
      const usersResult = await connection.query('SELECT Key, Value FROM users');
      const avatarMap = {}; // Map user ID -> avatar URL

      usersResult.toArray().forEach(row => {
        const rowData = row.toJSON();
        let rowUsers = rowData.Value || rowData.value;

        // Parse the JSON string to get array of users
        if (typeof rowUsers === 'string') {
          try {
            rowUsers = JSON.parse(rowUsers);
          } catch {
            rowUsers = [];
          }
        }

        // Extract avatar URLs
        if (Array.isArray(rowUsers)) {
          rowUsers.forEach(u => {
            const id = u.Id || u.id;
            const avatarUrl = u.AvatarUrl || u.avatarUrl || '';
            if (id && avatarUrl && !avatarMap[id]) {
              avatarMap[id] = avatarUrl;
            }
          });
        }
      });

      // Step 2: Load team structure from team_users table
      const result = await connection.query('SELECT Id, Users FROM team_users');
      const byTeam = {};
      result.toArray().forEach(row => {
        const team = row.toJSON();
        const teamId = team.Id || team.id;
        let users = team.Users || team.users || [];

        // Handle if Users is a string (JSON)
        if (typeof users === 'string') {
          try {
            users = JSON.parse(users);
          } catch {
            users = [];
          }
        }

        // Convert Vector to array if needed
        if (users && typeof users.toArray === 'function') {
          users = users.toArray().map(u => typeof u.toJSON === 'function' ? u.toJSON() : u);
        }

        // Filter for current users and map to expected format
        if (Array.isArray(users)) {
          byTeam[teamId] = users
            .filter(u => u.Current === true)
            .map(u => {
              const id = u.Id || u.id;
              return {
                id: id,
                displayName: u.DisplayName || u.displayName || '',
                avatarUrl: avatarMap[id] || '', // Get avatar from avatar map!
                roleTitle: u.RoleTitle || u.roleTitle || ''
              };
            });
        }
      });
      teamUsers.value = byTeam;
    } catch (error) {
      console.error('Error loading team users from DB:', error);
    }
  };

  /**
   * One PUT per selected sub-item. `items` is the *working* item list (the
   * page's loaded items) — it is what resolves each sub-item's parent type,
   * and is deliberately NOT the store's full item list (R13).
   *
   * @param {object} payload
   * @param {string[]} payload.subItemIds
   * @param {Array} payload.items
   * @param {string} payload.teamId
   * @param {string[]} payload.userIds
   * @returns {Promise<boolean>} true when every PUT succeeded
   */
  const assign = async ({ subItemIds, items, teamId, userIds }) => {
    assigning.value = true;

    const putObj = {
      teamId: String(teamId),
      userIds: (userIds || []).map(String)
    };

    try {
      const api = unref(apiClient);

      trackEvent?.('assignUsers', {
        action_type: 'button_click',
        apiUrl: unref(apiUrl),
        project_code: unref(projectCode),
        project_name: unref(projectName)
      });

      const selectedIds = [...(subItemIds || [])];
      const itemsToProcess = items || [];

      // Make API calls for each selected subItem
      for (const item of itemsToProcess) {
        for (const subItem of (item.subItems || [])) {
          if (selectedIds.includes(subItem.id)) {
            const itemType = item.type === 'IssueTemplate' ? 'issues' : 'forms';
            await api.put(
              `/api/${unref(projectCode)}/${itemType}/${subItem.id}/assignee`,
              putObj
            );
          }
        }
      }

      return true;
    } catch (error) {
      console.error("Error assigning items:", error);
      return false;
    } finally {
      assigning.value = false;
    }
  };

  return { teamUsers, loadTeamUsers, assigning, assign };
}
